// Персонаж: уровень за тренировки, фигура (последняя тренировка, неделя, восстановление, прокачка мышц)
// и S.P.E.C.I.A.L.
// Всё считает сервер (domain.character); здесь только раскраска и подписи.

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { PageHeader } from '@/components/layout'
import { MuscleLegend, MuscleMap } from '@/components/muscle-map/MuscleMap'
import { RANKS, readyIn, recoveryTone, weekTone, type MuscleTone } from '@/components/muscle-map/muscles'
import { Card, ErrorText, PageSpinner } from '@/components/ui'
import { cn } from '@/lib/cn'
import { formatDayMonth, formatNumber, formatSigned, pluralRu } from '@/lib/format'
import { useMuscleLabels } from '@/pages/workouts/api'

import { AchievementsCard } from './AchievementsCard'
import { useCharacter, type Character, type Stat } from './api'
import { useMascotHidden } from './mascot/logic'
import { WeeklyQuestsCard } from './WeeklyQuestsCard'
import { CharacterHistoryCard } from './CharacterHistoryCard'

type Mode = 'workout' | 'week' | 'recovery' | 'levels'

const MODES = [
  { value: 'workout', label: 'Тренировка' },
  { value: 'week', label: 'Неделя' },
  { value: 'recovery', label: 'Восстановление' },
  { value: 'levels', label: 'Прокачка' },
] as const

const LABELS: Record<Mode, string> = {
  workout: 'Мышцы последней тренировки',
  week: 'Нагрузка на мышцы за неделю',
  recovery: 'Восстановление мышц',
  levels: 'Прокачка мышц по рангам',
}

const LEGENDS: Record<Mode, MuscleTone[]> = {
  workout: ['primary', 'secondary', 'idle'],
  week: ['forgot', 'stale', 'low', 'ok', 'over'],
  recovery: ['fatigued', 'recovering', 'fresh'],
  levels: [...RANKS],
}

const SETS = ['подход', 'подхода', 'подходов'] as const
const DAYS = ['день', 'дня', 'дней'] as const

function LevelCard({ data }: { data: Character }) {
  const span = data.next_level_xp - data.level_xp
  const progress = span > 0 ? Math.min(1, (data.xp - data.level_xp) / span) : 1
  const last = data.last_workout
  return (
    <Card>
      <div className="flex items-center gap-3">
        <div className="grid size-12 shrink-0 place-items-center rounded-xl bg-accent-soft text-accent">
          <span className="text-xl font-bold leading-none">{data.level}</span>
          <span className="-mt-2 text-[9px] font-semibold tracking-wider">УР.</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="font-semibold">{data.title}</span>
            <span className="text-xs text-muted">
              {formatNumber(data.xp, { digits: 0 })} / {formatNumber(data.next_level_xp, { digits: 0 })} XP
            </span>
          </div>
          <div
            className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-surface-2"
            role="progressbar"
            aria-label="Опыт до следующего уровня"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
          >
            <div className="h-full rounded-full bg-accent" style={{ width: `${progress * 100}%` }} />
          </div>
        </div>
      </div>
      <p className="mt-3 rounded-lg bg-surface-2 px-3 py-2 text-sm">
        {last ? (
          <>
            {last.title}, {formatDayMonth(last.started_at)}: <span className="font-semibold text-accent">+{last.xp} XP</span>
            <span className="text-muted">
              {' '}
              · всего {data.workouts} {pluralRu(data.workouts, ['тренировка', 'тренировки', 'тренировок'])}
            </span>
          </>
        ) : (
          <>
            Опыт дают тренировки: 50 XP за тренировку, 5 за рабочий подход и бонусы за задания.{' '}
            <Link to="/workout" className="text-accent underline underline-offset-4">
              Начать тренировку
            </Link>
          </>
        )}
      </p>
    </Card>
  )
}

function MusclesCard({ data }: { data: Character }) {
  const muscle = useMuscleLabels()
  const last = data.last_workout
  // Пока режим не выбран — последняя тренировка, если она есть (данные могут прийти позже кэша).
  const [chosen, setMode] = useState<Mode | null>(null)
  const mode: Mode = chosen ?? (last ? 'workout' : 'week')
  const [selected, setSelected] = useState<string | null>(null)

  const tones = useMemo(() => {
    const out: Record<string, MuscleTone> = {}
    for (const [key, week] of Object.entries(data.week)) {
      out[key] =
        mode === 'week'
          ? weekTone(week.sets, week.days_since)
          : mode === 'recovery'
            ? recoveryTone(data.recovery?.[key]?.percent ?? 100)
            : mode === 'levels'
              ? (data.muscle_levels?.[key]?.rank ?? 'bronze')
              : (last?.muscles[key]?.role ?? 'idle')
    }
    return out
  }, [data.week, data.recovery, data.muscle_levels, last, mode])
  const lagging = Object.entries(data.muscle_levels ?? {})
    .filter(([, l]) => l.lagging)
    .sort(([, a], [, b]) => a.level - b.level)

  const info = (key: string) => {
    if (mode === 'workout') {
      const work = last?.muscles[key]
      if (!work) return 'в этой тренировке не работала'
      return `${work.role === 'primary' ? 'основная' : 'вспомогательная'}, ${work.sets} ${pluralRu(work.sets, SETS)}`
    }
    if (mode === 'recovery') {
      const rec = data.recovery?.[key]
      if (!rec || rec.ready_in_h == null) return 'свежая — можно нагружать'
      return `восстановлена на ${rec.percent} %, свежая ${readyIn(rec.ready_in_h)}`
    }
    if (mode === 'levels') {
      const lvl = data.muscle_levels?.[key]
      if (!lvl) return ''
      const left = formatNumber(lvl.next_level_sets - lvl.sets)
      return `${lvl.level} ур., ${lvl.rank_label} · ${formatNumber(lvl.sets)} ${pluralRu(Math.ceil(lvl.sets), SETS)}; до ${lvl.level + 1} ур. — ещё ${left}${lvl.lagging ? ' · отстаёт' : ''}`
    }
    const week = data.week[key]
    if (!week) return ''
    const ago =
      week.days_since == null
        ? 'больше 60 дней без нагрузки'
        : week.days_since === 0
          ? 'последний раз сегодня'
          : `последний раз ${week.days_since} ${pluralRu(week.days_since, DAYS)} назад`
    const sets = `${formatNumber(week.sets)} ${pluralRu(Math.ceil(week.sets), SETS)} за неделю`
    return `${sets}; ${ago}`
  }


  return (
    <Card title="Мышцы">
      {/* Четыре режима: на телефоне сеткой 2×2, шире — в строку (Tabs листается вбок и прячет последний). */}
      <div role="tablist" aria-label="Что показать на фигуре" className="grid grid-cols-2 gap-0.5 rounded-lg bg-surface-2 p-0.5 sm:grid-cols-4">
        {MODES.map((item) => (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={item.value === mode}
            onClick={() => setMode(item.value)}
            className={cn(
              'min-h-9 rounded-md px-2 text-sm font-medium transition-colors lg:min-h-8',
              item.value === mode ? 'bg-surface text-fg shadow-sm' : 'text-muted hover:text-fg',
            )}
          >
            {item.label}
          </button>
        ))}
      </div>
      <p className="mt-2 text-xs text-muted">
        {mode === 'workout'
          ? last
            ? `${last.title} · ${formatDayMonth(last.started_at)}`
            : 'Завершённых тренировок ещё нет'
          : mode === 'week'
            ? 'Последние 7 дней: основная мышца — подход, вспомогательная — половина'
            : mode === 'levels'
              ? 'Все подходы за историю: основная мышца — подход, вспомогательная — половина'
              : (data.recovery_note ?? 'Крупные мышцы восстанавливаются ~72 ч, средние ~48 ч, мелкие ~36 ч')}
      </p>
      <MuscleMap
        sex={data.sex}
        tones={tones}
        selected={selected}
        onSelect={setSelected}
        label={LABELS[mode]}
        className="mx-auto mt-2 w-full max-w-md"
      />
      <p className="mt-2 min-h-10 rounded-lg bg-surface-2 px-3 py-2 text-sm" aria-live="polite">
        {selected ? (
          <>
            <span className="font-medium">{muscle(selected)}</span> · {info(selected)}
          </>
        ) : (
          <span className="text-muted">Нажми на мышцу, чтобы увидеть подробности</span>
        )}
      </p>
      <MuscleLegend tones={LEGENDS[mode]} className="mt-3" />
      {mode === 'levels' && lagging.length ? (
        <div className="mt-3 flex flex-wrap items-center gap-1.5 text-sm">
          <span className="text-muted">Отстают:</span>
          {lagging.map(([key, l]) => (
            <button
              key={key}
              type="button"
              onClick={() => setSelected(key)}
              className="rounded-full border border-border px-2.5 py-0.5 hover:border-accent hover:text-accent"
            >
              {muscle(key)} · {l.level} ур.
            </button>
          ))}
        </div>
      ) : null}
      <p className="mt-3 border-t border-border pt-3 text-sm text-muted">
        Фигура: {data.sex === 'female' ? 'женская' : 'мужская'} ·{' '}
        <Link to="/settings/profile" className="text-accent underline underline-offset-4">
          пол в профиле
        </Link>
      </p>
    </Card>
  )
}

function trendText(stat: Stat): { text: string; tone: 'up' | 'down' | 'flat' } | null {
  if (stat.value == null || stat.month_ago == null) return null
  const delta = Math.round((stat.value - stat.month_ago) * 10) / 10
  if (delta === 0) return { text: 'без изменений за месяц', tone: 'flat' }
  return { text: `${formatSigned(delta)} за месяц`, tone: delta > 0 ? 'up' : 'down' }
}

function StatRow({ stat }: { stat: Stat }) {
  const value = stat.value
  const trend = trendText(stat)
  const whole = value == null ? 0 : Math.floor(value)
  const part = value == null ? 0 : value - whole
  return (
    <li className="grid grid-cols-[2rem_1fr_auto] items-center gap-x-3 gap-y-0.5 border-t border-border py-2.5 first:border-t-0">
      <span aria-hidden className="row-span-2 grid size-8 place-items-center rounded-lg bg-surface-2 font-bold">
        {stat.key}
      </span>
      <span className="text-sm font-semibold">{stat.name}</span>
      <span className="text-right text-lg font-bold" aria-label={value == null ? 'нет данных' : `${formatNumber(value)} из 10`}>
        {value == null ? '—' : formatNumber(value)}
      </span>
      <span className="text-xs text-muted">{value == null ? stat.hint : (stat.detail ?? stat.source)}</span>
      <span
        className={cn(
          'text-right text-xs',
          trend?.tone === 'up' ? 'text-accent' : trend?.tone === 'down' ? 'text-danger' : 'text-muted',
        )}
      >
        {trend?.text}
      </span>
      <span aria-hidden className="col-start-2 col-end-4 mt-1 flex gap-0.5">
        {Array.from({ length: 10 }, (_, i) => (
          <span key={i} className="relative h-1.5 flex-1 overflow-hidden rounded-sm bg-surface-2">
            {i < whole ? <span className="absolute inset-0 bg-accent" /> : null}
            {i === whole && part > 0 ? (
              <span className="absolute inset-y-0 left-0 bg-accent opacity-50" style={{ width: `${part * 100}%` }} />
            ) : null}
          </span>
        ))}
      </span>
    </li>
  )
}

function SpecialCard({ stats }: { stats: Stat[] }) {
  return (
    <Card title="S.P.E.C.I.A.L." subtitle="Шкала 1–10 по данным последних недель: без тренировок и замеров медленно падает.">
      <ul>
        {stats.map((stat) => (
          <StatRow key={stat.key} stat={stat} />
        ))}
      </ul>
    </Card>
  )
}

function MascotToggle() {
  const [hidden, setHidden] = useMascotHidden()
  return (
    <label className="mt-4 flex min-h-11 items-center gap-2 text-sm text-muted">
      <input type="checkbox" className="h-5 w-5" checked={!hidden} onChange={(e) => setHidden(!e.target.checked)} />
      Показывать маскота в углу экрана
    </label>
  )
}

export function CharacterPage() {
  const query = useCharacter()
  return (
    <>
      <PageHeader title="Персонаж" description="Прокачка по настоящим тренировкам и замерам." />
      {query.isPending ? (
        <PageSpinner />
      ) : query.data ? (
        <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
          <div className="grid min-w-0 gap-4">
            <LevelCard data={query.data} />
            <WeeklyQuestsCard />
            <MusclesCard data={query.data} />
          </div>
          <SpecialCard stats={query.data.special} />
        </div>
      ) : (
        <ErrorText error={query.error}>Не удалось загрузить персонажа.</ErrorText>
      )}
      {query.data ? (
        <>
          <CharacterHistoryCard />
          <AchievementsCard className="mt-4" />
        </>
      ) : null}
      <MascotToggle />
    </>
  )
}
